import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

const PayoutRequestItem = ({ request, onProcess }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCopyDetails = async () => {
    toast.loading('Fetching details...');
    try {
      const functions = getFunctions();
      const getArtistPayoutDetails = httpsCallable(functions, 'getArtistPayoutDetails');
      const result = await getArtistPayoutDetails({ artistId: request.artistId });
      
      await navigator.clipboard.writeText(result.data.details);
      toast.dismiss();
      toast.success('Full payout details copied to clipboard!');
    } catch (error) {
      console.error("Failed to copy details:", error);
      toast.dismiss();
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleAction = async (action) => {
    let reason = '';
    if (action === 'deny') {
      reason = prompt("Please provide a reason for denying this request:");
      if (reason === null) return;
    }
    setIsProcessing(true);
    await onProcess(request.id, action, reason);
    setIsProcessing(false);
  };
  
  const payoutDetails = request.payoutDetails;
  const detailsString = payoutDetails?.paypalEmail 
    ? `PayPal: ${payoutDetails.paypalEmail}`
    : `Bank: ...${payoutDetails?.bankDetails?.accountNumber?.slice(-4)}`;

  return (
    <div className="bg-white p-6 rounded-lg shadow border border-gray-200 mb-4 transition-all hover:shadow-md">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="font-bold text-xl text-slate-800">{request.artistName}</h3>
          <p className="text-sm text-gray-500 font-mono">ID: {request.artistId}</p>
          <div className="text-sm text-gray-600 mt-3 flex items-center gap-x-4 bg-gray-50 p-2 rounded">
            <span className="font-mono font-semibold">{detailsString}</span>
            <button 
              onClick={handleCopyDetails}
              className="text-xs font-bold text-blue-600 hover:text-blue-800 uppercase tracking-tight"
            >
              Copy Full Info
            </button>
          </div>
        </div>
        <div className="text-right">
          <p className="text-3xl font-black text-green-600">${request.requestedAmount.toFixed(2)}</p>
          <p className="text-xs text-gray-400 mt-1">
            {request.createdAt?.toDate().toLocaleString() || 'Pending...'}
          </p>
        </div>
      </div>
      {request.notes && (
        <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-200 text-sm italic text-gray-700">
          Notes: "{request.notes}"
        </div>
      )}
      <div className="flex gap-4 mt-6">
        <button 
          onClick={() => handleAction('approve')} 
          disabled={isProcessing}
          className="bg-slate-800 text-white font-bold py-3 px-6 rounded-lg hover:bg-black disabled:bg-gray-300 flex-1 transition-colors"
        >
          {isProcessing ? 'Processing...' : 'Approve Payout'}
        </button>
        <button 
          onClick={() => handleAction('deny')} 
          disabled={isProcessing}
          className="bg-white text-red-600 border border-red-200 font-bold py-3 px-6 rounded-lg hover:bg-red-50 disabled:bg-gray-50 flex-1 transition-colors"
        >
          Deny
        </button>
      </div>
    </div>
  );
};

const PayoutsTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const db = getFirestore();

  useEffect(() => {
    const q = query(
      collection(db, 'serviceRequests'),
      where('type', '==', 'payout_request'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const processPayout = async (requestId, action, reason) => {
    try {
      const functions = getFunctions();
      const processFunction = httpsCallable(functions, 'processPayoutRequest');
      await processFunction({ requestId, action, reason });
      toast.success(`Payout ${action === 'approve' ? 'approved' : 'denied'}.`);
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading payout queue...</div>;
  if (requests.length === 0) return <div className="p-20 text-center text-gray-400 font-medium">No pending payouts. Check back later!</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6">Pending Artist Withdrawals</h2>
      {requests.map(req => (
        <PayoutRequestItem key={req.id} request={req} onProcess={processPayout} />
      ))}
    </div>
  );
};

export default PayoutsTab;